import type { ReactNode } from "react";
import { Inbox, type LucideIcon } from "lucide-react";
import { STAGE_LABEL, type Stage } from "./api";

/**
 * Peças visuais do painel.
 *
 * Regra que vale para todas: estado nunca e comunicado so por numero ou so
 * por cor. Score vira barra, urgencia vira listra, controle vira pilula com
 * texto. Quem opera isso oito horas por dia le por forma, nao por leitura.
 */

const LISTRA = {
  accent: "bg-accent",
  warm: "bg-warm",
  danger: "bg-danger",
  ok: "bg-ok",
} as const;

export function Card({
  children,
  className = "",
  stripe,
}: {
  children: ReactNode;
  className?: string;
  stripe?: "accent" | "warm" | "danger" | "ok";
}) {
  /*
   * A listra e uma barra por dentro, e nao `border-left`.
   *
   * Com canto arredondado, borda lateral colorida entorta junto com a curva e
   * vira um parentese. A barra fica reta e ainda diz o estado de relance.
   */
  return (
    <div
      className={`relative bg-surface border border-rule rounded-xl shadow-[var(--shadow-card)] ${
        stripe ? "overflow-hidden" : ""
      } ${className}`}
    >
      {stripe && (
        <span
          aria-hidden
          className={`absolute left-0 top-0 bottom-0 w-[3px] ${LISTRA[stripe]}`}
        />
      )}
      {children}
    </div>
  );
}

/**
 * Iniciais num circulo, com cor tirada do proprio nome.
 *
 * A mesma pessoa tem sempre a mesma cor em qualquer tela -- o olho reconhece
 * o lead antes de ler o nome, que e o que importa numa lista que muda sozinha.
 */
export function Avatar({
  nome,
  tamanho = "md",
}: {
  nome: string | null;
  tamanho?: "sm" | "md";
}) {
  const texto = (nome ?? "").trim();
  const iniciais =
    texto
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]!.toUpperCase())
      .join("") || "?";
  let h = 0;
  for (const c of texto) h = (h * 31 + c.charCodeAt(0)) % 360;
  const dim = tamanho === "sm" ? "w-7 h-7 text-[11px]" : "w-9 h-9 text-xs";
  return (
    <span
      className={`${dim} shrink-0 rounded-full grid place-items-center font-semibold`}
      style={{
        background: `hsl(${h} 45% 22%)`,
        color: `hsl(${h} 70% 78%)`,
      }}
      aria-hidden
    >
      {iniciais}
    </span>
  );
}

/** Titulo de bloco dentro de uma tela, com espaco para uma acao a direita. */
export function Secao({
  titulo,
  descricao,
  acao,
  children,
  className = "",
}: {
  titulo: ReactNode;
  descricao?: ReactNode;
  acao?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={className}>
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">{titulo}</h2>
          {descricao && (
            <p className="text-xs text-ink-3 mt-0.5 max-w-prose">{descricao}</p>
          )}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

/**
 * Abas de uma tela.
 *
 * Os rotulos vem prontos, em minuscula de frase. A versao anterior usava
 * `capitalize` do CSS, que poe maiuscula em toda palavra -- e saia "Números
 * De WhatsApp", "Imóveis E Fotos".
 */
export function Abas<T extends string>({
  abas,
  atual,
  onChange,
}: {
  abas: { id: T; rotulo: ReactNode; icone?: LucideIcon; alerta?: number }[];
  atual: T;
  onChange: (id: T) => void;
}) {
  return (
    /*
     * Rola so na horizontal, e sem barra.
     *
     * O sublinhado da aba ativa passava 1px da borda de baixo; com
     * `overflow-x: auto` o navegador tambem liga a rolagem vertical, e aparecia
     * uma barra de rolagem ao lado das abas. Agora o sublinhado fica em cima da
     * linha, dentro do contêiner. No celular as abas continuam rolando com o dedo.
     */
    <div className="sem-barra flex gap-1 overflow-x-auto overflow-y-hidden border-b border-rule">
      {abas.map(({ id, rotulo, icone: Icone, alerta }) => {
        const ativa = id === atual;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            className={`relative flex items-center gap-2 px-3 pt-2 pb-2.5 text-sm whitespace-nowrap rounded-t-lg transition-colors ${
              ativa ? "text-ink" : "text-ink-3 hover:text-ink hover:bg-surface-2"
            }`}
          >
            {Icone && <Icone size={15} strokeWidth={2} className={ativa ? "text-accent" : ""} />}
            {rotulo}
            {alerta !== undefined && alerta > 0 && (
              <span className="min-w-5 h-5 px-1.5 rounded-full bg-danger-dim text-danger text-[11px] font-semibold grid place-items-center">
                {alerta}
              </span>
            )}
            {ativa && (
              <span className="absolute left-2 right-2 bottom-0 h-0.5 rounded-full bg-accent" />
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Controle segmentado: poucas opcoes exclusivas, lado a lado. */
export function Segmentos<T extends string | number>({
  opcoes,
  atual,
  onChange,
}: {
  opcoes: { id: T; rotulo: ReactNode }[];
  atual: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-rule">
      {opcoes.map((o) => (
        <button
          key={String(o.id)}
          onClick={() => onChange(o.id)}
          className={`px-3 py-1 text-sm rounded-md transition-colors ${
            o.id === atual
              ? "bg-surface-3 text-ink shadow-sm"
              : "text-ink-3 hover:text-ink"
          }`}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

/**
 * Forma do que esta carregando.
 *
 * "Carregando..." em texto faz a tela pular quando o conteudo chega; o
 * esqueleto ocupa o lugar certo e a troca e suave.
 */
export function Carregando({
  linhas = 3,
  altura = "h-16",
}: {
  linhas?: number;
  altura?: string;
}) {
  return (
    <div className="space-y-2" aria-busy aria-label="carregando">
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className={`esqueleto ${altura}`} style={{ opacity: 1 - i * 0.2 }} />
      ))}
    </div>
  );
}

/**
 * Explicacao curta dos dois numeros.
 *
 * Fica no `title` de toda barra: quem opera nao vai ler documentacao, e um
 * numero sem significado e um numero que a pessoa ignora.
 */
export const SCORE_HELP = {
  fit: [
    "FIT — vale a pena, e a conta fecha?",
    "",
    "Soma o que já descobrimos, com peso maior no que é caro de saber:",
    "dinheiro 30 · propósito 15 · quem decide 15 · prazo 15 · região 10 ·",
    "preferências 10 · origem 5.",
    "",
    "Depois trava: entrada e parcela baixas demais limitam a 25, mesmo com",
    "tudo respondido. Restrição no nome limita a 30.",
  ].join("\n"),
  temp: [
    "TEMPERATURA — ele está quente agora?",
    "",
    "Volume de respostas 40 · perguntas que ELE fez 20 ·",
    "velocidade de resposta 25 · reagiu bem a um imóvel 15.",
    "",
    "Decai com o silêncio: até 24h ×0,85 · até 72h ×0,60 ·",
    "até 7 dias ×0,35 · depois ×0,15.",
  ].join("\n"),
} as const;

/** Barra + numero. A barra e o que se le de relance na lista. */
export function Score({
  value,
  label,
  tone = "accent",
}: {
  value: number;
  label: string;
  tone?: "accent" | "warm";
}) {
  const color = tone === "warm" ? "bg-warm" : "bg-accent";
  const ajuda = label === "fit" ? SCORE_HELP.fit : label === "temp" ? SCORE_HELP.temp : undefined;
  return (
    <div className="flex items-center gap-2 min-w-0" title={ajuda}>
      <span className="text-[11px] font-medium text-ink-3 w-9 shrink-0">
        {label}
      </span>
      <div className="h-1.5 flex-1 min-w-8 bg-surface-3 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-[width] duration-500`}
          style={{ width: `${Math.max(2, value)}%` }}
        />
      </div>
      <span className="text-xs font-medium tabular-nums text-ink-2 w-7 text-right shrink-0">
        {value}
      </span>
    </div>
  );
}

export function Pill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "accent" | "warm" | "danger" | "ok";
}) {
  const tones = {
    neutral: "bg-surface-3 text-ink-2 ring-rule-2",
    accent: "bg-accent-dim text-accent ring-accent/25",
    warm: "bg-warm-dim text-warm ring-warm/25",
    danger: "bg-danger-dim text-danger ring-danger/25",
    ok: "bg-ok-dim text-ok ring-ok/25",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 ring-inset text-[11px] font-medium whitespace-nowrap ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function ControlPill({ control }: { control: string | null }) {
  if (control === "human")
    return <Pill tone="accent">corretor no comando</Pill>;
  if (control === "pending_human")
    return (
      <Pill tone="warm">
        <span className="pulse">●</span> esperando humano
      </Pill>
    );
  return <Pill tone="ok">IA atendendo</Pill>;
}

export function StagePill({ stage }: { stage: Stage }) {
  const tone =
    stage === "ganho"
      ? "ok"
      : stage === "perdido"
        ? "danger"
        : stage === "qualificado" ||
            stage === "visita_agendada" ||
            stage === "proposta"
          ? "accent"
          : "neutral";
  return <Pill tone={tone as never}>{STAGE_LABEL[stage]}</Pill>;
}

export function Stat({
  label,
  value,
  hint,
  tone,
  icone: Icone,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "warm" | "danger";
  icone?: LucideIcon;
}) {
  const color =
    tone === "danger" ? "text-danger" : tone === "warm" ? "text-warm" : "text-ink";
  const chip =
    tone === "danger"
      ? "bg-danger-dim text-danger"
      : tone === "warm"
        ? "bg-warm-dim text-warm"
        : "bg-accent-dim text-accent";
  return (
    <div className="bg-surface border border-rule rounded-xl shadow-[var(--shadow-card)] px-4 py-3.5 min-w-0">
      <div className="flex items-center gap-2">
        {Icone && (
          <span className={`w-6 h-6 rounded-md grid place-items-center shrink-0 ${chip}`}>
            <Icone size={14} strokeWidth={2.2} />
          </span>
        )}
        <div className="text-xs font-medium text-ink-3 truncate">{label}</div>
      </div>
      <div className={`text-[28px] leading-none font-semibold tracking-tight tabular-nums mt-3 ${color}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-ink-3 mt-1.5 truncate">{hint}</div>}
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = "default",
  disabled,
  className = "",
  icone: Icone,
  title,
}: {
  children?: ReactNode;
  onClick?: () => void;
  variant?: "default" | "primary" | "ghost" | "danger";
  disabled?: boolean;
  className?: string;
  icone?: LucideIcon;
  title?: string;
}) {
  const variants = {
    default:
      "bg-surface-2 border-rule-2 text-ink hover:bg-surface-3 hover:border-white/20",
    primary:
      "bg-accent border-accent text-ground font-semibold shadow-[0_0_0_1px_rgb(63_203_193/0.3),0_4px_14px_-4px_rgb(63_203_193/0.5)] hover:brightness-110",
    ghost: "bg-transparent border-transparent text-ink-2 hover:text-ink hover:bg-surface-2",
    danger: "bg-transparent border-danger/40 text-danger hover:bg-danger-dim",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center justify-center gap-1.5 px-3.5 h-9 rounded-lg border text-sm transition-all active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none ${variants[variant]} ${className}`}
    >
      {Icone && <Icone size={15} strokeWidth={2.2} />}
      {children}
    </button>
  );
}

export function Empty({
  children,
  icone: Icone = Inbox,
}: {
  children: ReactNode;
  icone?: LucideIcon;
}) {
  return (
    <div className="flex flex-col items-center gap-3 text-center py-14 px-6 text-ink-3 text-sm border border-dashed border-rule-2 rounded-xl bg-surface/40">
      <span className="w-10 h-10 rounded-full bg-surface-2 grid place-items-center text-ink-3">
        <Icone size={18} />
      </span>
      <div className="max-w-md">{children}</div>
    </div>
  );
}

/** Rotulo de campo de qualificacao em portugues legivel. */
export const FIELD_LABEL: Record<string, string> = {
  telefone_contato: "Ligar neste número",
  empreendimento_interesse: "Empreendimento do anúncio",
  conhece_regiao: "Conhece a região",
  regiao_interesse: "Região",
  motivo_regiao: "Por que essa região",
  proposito: "Propósito",
  prazo_decisao: "Prazo",
  ja_visitou_outros: "Já visitou outros",
  entrada_disponivel: "Entrada",
  parcela_mensal: "Parcela/mês",
  renda_familiar: "Renda familiar",
  usa_fgts: "FGTS",
  imovel_para_vender: "Imóvel pra vender",
  financiamento_aprovado: "Financiamento aprovado",
  nome_limpo: "Nome limpo",
  tempo_carteira: "Tempo de carteira",
  primeiro_imovel: "Primeiro imóvel",
  compradores: "Compradores",
  decisor: "Quem decide",
  tipologia: "Tipologia",
  vagas: "Vagas",
  aceita_planta: "Aceita planta",
  tem_pet: "Pet",
  mobiliado: "Mobiliado",
};

/** Grupos na mesma ordem da conversa: região → propósito → dinheiro → ... */
export const FIELD_GROUPS: { title: string; fields: string[] }[] = [
  { title: "Região", fields: ["conhece_regiao", "regiao_interesse", "motivo_regiao"] },
  { title: "Propósito", fields: ["proposito", "prazo_decisao", "ja_visitou_outros"] },
  {
    title: "Dinheiro",
    fields: [
      "entrada_disponivel",
      "parcela_mensal",
      "renda_familiar",
      "usa_fgts",
      "imovel_para_vender",
      "financiamento_aprovado",
    ],
  },
  {
    title: "Viabilidade",
    fields: ["nome_limpo", "tempo_carteira", "primeiro_imovel", "compradores"],
  },
  {
    title: "Decisão",
    fields: ["decisor", "telefone_contato", "empreendimento_interesse"],
  },
  {
    title: "Preferências",
    fields: ["tipologia", "vagas", "aceita_planta", "tem_pet", "mobiliado"],
  },
];

const MONEY_FIELDS = new Set([
  "entrada_disponivel",
  "parcela_mensal",
  "renda_familiar",
]);

export function formatFact(field: string, value: unknown): string {
  const raw = typeof value === "string" ? value : JSON.stringify(value);
  if (MONEY_FIELDS.has(field)) {
    const n = Number(String(raw).replace(/[^0-9]/g, ""));
    if (Number.isFinite(n) && n > 0) {
      return n.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 0,
      });
    }
  }
  return raw;
}
